import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { FileXls, ArrowClockwise } from "@phosphor-icons/react";

const EVENTS = [
  ["", "Todos los eventos"], ["login", "Inicio de sesión"], ["logout", "Cierre de sesión"], ["presence", "Cambio de estado"],
  ["reconnect", "Reconexión automática"], ["auto_offline", "Desconexión por inactividad"],
];
const sel = "h-9 rounded-md border border-input bg-background px-2 text-sm";

function Filters({ f, setF, users }) {
  const set = (k) => (e) => setF((p) => ({ ...p, [k]: e.target.value }));
  return (
    <div className="flex flex-wrap gap-2 items-end">
      <select className={sel} value={f.user_id} onChange={set("user_id")} data-testid="connlog-user">
        <option value="">Todos los usuarios</option>
        {users.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
      </select>
      <select className={sel} value={f.event} onChange={set("event")} data-testid="connlog-event">
        {EVENTS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
      </select>
      <Input type="date" className="h-9 w-40" value={f.date_from} onChange={set("date_from")} data-testid="connlog-from" />
      <Input type="date" className="h-9 w-40" value={f.date_to} onChange={set("date_to")} data-testid="connlog-to" />
    </div>
  );
}

export const ConnectionLog = () => {
  const [f, setF] = useState({ user_id: "", event: "", date_from: "", date_to: "" });
  const [users, setUsers] = useState([]);
  const [data, setData] = useState({ total: 0, rows: [] });
  const params = Object.fromEntries(Object.entries(f).filter(([, v]) => v));
  const load = useCallback(() => {
    api.get("/admin/connection-logs", { params }).then(({ data: d }) => setData(d)).catch(() => toast.error("No se pudo cargar el registro"));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(params)]);
  useEffect(() => { api.get("/users").then(({ data: d }) => setUsers(d)).catch(() => {}); }, []);
  useEffect(() => { load(); }, [load]);
  const exportXlsx = async () => {
    try {
      const r = await api.get("/admin/connection-logs/export", { params, responseType: "blob" });
      const a = Object.assign(document.createElement("a"), { href: URL.createObjectURL(r.data), download: "SDM-Concierge-Conexiones.xlsx" });
      a.click(); URL.revokeObjectURL(a.href);
    } catch { toast.error("No se pudo exportar"); }
  };
  return (
    <div className="space-y-4" data-testid="connection-log">
      <div>
        <h2 className="text-lg font-semibold">Registro de conexiones</h2>
        <p className="text-xs text-muted-foreground">Inicios y cierres de sesión y cambios de estado de cada usuario (hora de RD). Se guarda 1 año.</p>
      </div>
      <div className="flex flex-wrap gap-2 items-end justify-between">
        <Filters f={f} setF={setF} users={users} />
        <div className="flex gap-2">
          <Button variant="outline" size="sm" className="gap-1" onClick={load} data-testid="connlog-refresh"><ArrowClockwise size={14} /> Actualizar</Button>
          <Button size="sm" className="gap-1" onClick={exportXlsx} data-testid="connlog-export"><FileXls size={14} /> Exportar Excel</Button>
        </div>
      </div>
      <div className="text-xs text-muted-foreground" data-testid="connlog-total">
        {data.total} eventos{data.total > data.rows.length ? ` · mostrando los ${data.rows.length} más recientes (el Excel trae todos)` : ""}
      </div>
      <div className="border border-border rounded-lg overflow-x-auto">
        <table className="w-full text-xs">
          <thead className="bg-secondary/60 text-left">
            <tr>{["Fecha y hora", "Usuario", "Rol", "Evento", "Estado", "IP"].map((h) => <th key={h} className="px-3 py-2 font-semibold whitespace-nowrap">{h}</th>)}</tr>
          </thead>
          <tbody className="divide-y divide-border">
            {data.rows.map((r) => (
              <tr key={r.id} data-testid={`connlog-row-${r.id}`}>
                <td className="px-3 py-1.5 whitespace-nowrap font-mono">{r.at}</td>
                <td className="px-3 py-1.5"><div>{r.name}</div><div className="text-muted-foreground">{r.email}</div></td>
                <td className="px-3 py-1.5">{r.role}</td>
                <td className="px-3 py-1.5 whitespace-nowrap">{r.event_label}</td>
                <td className="px-3 py-1.5">{r.presence}</td>
                <td className="px-3 py-1.5 font-mono">{r.ip}</td>
              </tr>
            ))}
            {data.rows.length === 0 && <tr><td colSpan={6} className="px-3 py-6 text-center text-muted-foreground">Sin eventos para estos filtros.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default ConnectionLog;
